import type { StoryResponse } from '../../api/types';

interface Props {
  storyA: StoryResponse;
  storyB: StoryResponse;
}

function StoryCard({
  story,
  label,
  bgClass,
}: {
  story: StoryResponse;
  label: string;
  bgClass: string;
}) {
  return (
    <div className={`rounded-lg p-4 ${bgClass}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="font-semibold text-gray-700">{label}</span>
        <span className="text-xs text-gray-500 font-mono">
          {story.id.slice(0, 8)}...
        </span>
      </div>

      <p className="text-sm text-gray-700 mb-4 line-clamp-4">{story.rawText}</p>

      <div className="space-y-3 text-sm">
        {/* Location */}
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Location</div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{story.location.city}</span>
            {story.location.venue && (
              <span className="text-gray-600">@ {story.location.venue}</span>
            )}
          </div>
          {story.location.microCues.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {story.location.microCues.map((c, i) => (
                <span
                  key={i}
                  className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Time */}
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</div>
          <div className="text-gray-700">
            {story.time.date || story.time.dayPart || 'Not specified'}
            {story.time.time && ` at ${story.time.time}`}
          </div>
        </div>

        {/* Event */}
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Event</div>
          <div className="text-gray-700">
            {story.event.trigger && (
              <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs mr-1">
                {story.event.trigger}
              </span>
            )}
            {story.event.object && (
              <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-xs">
                {story.event.object}
              </span>
            )}
            {!story.event.trigger && !story.event.object && (
              <span className="text-gray-400">None specified</span>
            )}
          </div>
        </div>

        {/* Person Description */}
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Person (them)
          </div>
          {story.themProfile.descriptors.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {story.themProfile.descriptors.map((d, i) => (
                <span
                  key={i}
                  className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded"
                >
                  {d}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-gray-400 text-xs">No descriptors</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SideBySide({ storyA, storyB }: Props) {
  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Story Comparison</h2>

      <div className="grid md:grid-cols-2 gap-4">
        <StoryCard story={storyA} label="Story A (Query)" bgClass="bg-blue-50" />
        <StoryCard story={storyB} label="Story B (Match)" bgClass="bg-green-50" />
      </div>

      {/* Mirror arrows */}
      <div className="mt-4 pt-4 border-t text-center text-sm text-gray-500">
        <div className="flex items-center justify-center gap-2">
          <span>A.them</span>
          <span className="text-xl">↔</span>
          <span>B.you</span>
          <span className="mx-4">|</span>
          <span>B.them</span>
          <span className="text-xl">↔</span>
          <span>A.you</span>
        </div>
        <div className="text-xs text-gray-400 mt-1">
          Mirror consistency: descriptions should align between perspectives
        </div>
      </div>
    </div>
  );
}
